import { requestApi } from '@/api';
import {
  emailTemplateImageId,
  EMAIL_TEMPLATE_IMAGE_UPLOAD_MAX_BYTES,
  EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_BYTES,
  EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_DIMENSION
} from '@strenforcement/domain/licenseStatusEmail';

/** Uploads branding through the authenticated municipality endpoint. */
export async function uploadEmailImage(cityId, file) {
  if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > EMAIL_TEMPLATE_IMAGE_UPLOAD_MAX_BYTES) {
    throw new Error('Choose a PNG or JPEG image no larger than 2 MB.');
  }

  const body = new FormData();
  body.append('file', file);

  return requestApi(`/v1/cities/${encodeURIComponent(cityId)}/email-template-images`, {
    method: 'POST',
    body
  });
}

/** Resolves a stored image for the editor without persisting a browser-only URL. */
export async function loadEmailImage(cityId, source) {
  const imageId = emailTemplateImageId(source);

  if (!imageId) throw new Error('Invalid email image reference.');

  const result = await requestApi(`/v1/cities/${encodeURIComponent(cityId)}/email-template-images/${imageId}`);
  return result.dataUrl;
}

/** Reads a browser-selected file or resized PNG without making an upload request. */
function readDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read the image.'));
    reader.readAsDataURL(blob);
  });
}

/** Decodes a selected image so its pixels can be resized and its metadata discarded. */
function decodeImage(source) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Choose a valid PNG or JPEG image.'));
    image.src = source;
  });
}

/** Encodes the canvas as PNG, including browsers that report encoding failure as null. */
function canvasPng(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Unable to prepare this image.'));
    }, 'image/png');
  });
}

/** Prepares a small, self-contained PNG; the API validates the bytes again before saving. */
export async function createEmbeddedEmailImage(file) {
  if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > EMAIL_TEMPLATE_IMAGE_UPLOAD_MAX_BYTES) {
    throw new Error('Choose a PNG or JPEG image no larger than 2 MB.');
  }

  const image = await decodeImage(await readDataUrl(file));
  if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 20000000) {
    throw new Error('Choose an image with no more than 20 million pixels.');
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image processing is unavailable in this browser.');

  const originalSize = Math.max(image.naturalWidth, image.naturalHeight);
  let size = Math.min(originalSize, EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_DIMENSION);
  let blob;

  while (!blob || blob.size > EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_BYTES) {
    canvas.width = Math.max(1, Math.round(image.naturalWidth * size / originalSize));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * size / originalSize));
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    blob = await canvasPng(canvas);

    if (blob.size <= EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_BYTES) break;
    if (size <= 200) throw new Error('This image is too detailed for email. Please choose a smaller or simpler image.');

    size = Math.max(200, Math.floor(size * 0.8));
  }

  const random = new Uint8Array(16);
  if (!window.crypto?.getRandomValues) {
    throw new Error('Image insertion is unavailable in this browser.');
  }

  window.crypto.getRandomValues(random);
  const id = `${Array.from(random, byte => byte.toString(16).padStart(2, '0')).join('')}.png`;

  return {
    source: `email-embedded:${id}`,
    dataUrl: await readDataUrl(blob)
  };
}
